import formatter from './formatter';
import { sortString, sortDate, sortNumber } from './sort';
import get from 'lodash/get';
import find from 'lodash/find';
import isNumber from 'lodash/isNumber';
import GetDataPointOptions from '../models/utils/GetDataPointOptions';
import { translate } from '../Components/Translate';

export const getDataPoint = (options: GetDataPointOptions) => {
    const {
        dataConfig,
        shareClass,
        locale,
        fundCurrency,
        defaultNoDataPlaceholder,
        dataStore,
        hasPerformance = true,
    } = options;
    let { find: findObj } = dataConfig;
    const {
        property,
        code,
        ccy,
        classification,
        periodicity,
        key,
        index,
        startIndex = 0,
        endIndex,
        format,
        sort,
        noDataPlaceholder = defaultNoDataPlaceholder,
        performanceCheck,
    } = dataConfig;

    if (performanceCheck && !hasPerformance) {
        return noDataPlaceholder;
    }

    // Translate find object values where possible
    if (findObj) {
        const findKeys = Object.keys(findObj);
        if (findKeys) {
            findObj = findKeys.reduce(
                (obj, findKey) => {
                    /**
                     * Due to FundPress limitations
                     * properties pub fields are translatable
                     * AND
                     * ONLY the label field for allocations, statistics & timeseries are translatable.
                     */
                    if (property === 'properties_pub' || findKey === 'label') {
                        obj[findKey] = translate(
                            dataStore.translations,
                            findObj[findKey]
                        );
                    } else {
                        obj[findKey] = findObj[findKey];
                    }
                    return obj;
                },
                {} as any
            );
        }
    }

    if (property === 'properties_pub') {
        const value = get(
            shareClass,
            `${property}.${code}.${key || 'value'}`,
            noDataPlaceholder
        );

        return format
            ? formatter({
                  dataStore,
                  locale,
                  value,
                  type: format.type,
                  currency: fundCurrency,
                  options: format.options,
              })
            : value;
    }

    if (property) {
        const dataSet = get(shareClass, property, []);

        // We should end up with one data object, so get the first item in array
        const data = find(dataSet, {
            ...(code && { code }),
            ...(ccy && { ccy }),
            ...(classification && { classification }),
            ...(periodicity && { periodicity }),
        });

        // Get the values from data
        let values: Array<any> = (data || {}).values || [];

        // Slice values to convert from observable array for compatibility with re-charts & sorting
        values = values.slice();

        // Sort by type
        if (sort && sort.type === 'date') {
            values.sort((a, b) => sortDate(a[sort.key], b[sort.key]));
        }

        if (sort && sort.type === 'string') {
            values.sort((a, b) => sortString(a[sort.key], b[sort.key]));
        }

        if (sort && sort.type === 'number') {
            values.sort((a, b) => sortNumber(a[sort.key], b[sort.key]));
        }

        // Sort direction
        if (sort && sort.direction === 'DESC') {
            values.reverse();
        }

        // Sort by a specified order
        if (sort && sort.order && sort.order.length > 0) {
            values = values
                .map(value => {
                    const orderIndex = sort.order.indexOf(value[sort.key]);
                    value.order =
                        orderIndex > -1 ? orderIndex : sort.order.length;
                    return value;
                })
                .sort((a, b) => sortNumber(a.order, b.order));
        }

        // Slice values using start/end index
        const offsetStartIndex = values.length + endIndex;

        const isTrailing = typeof endIndex === 'number' && endIndex < 0;
        values = isTrailing
            ? values.slice(Math.max(0, offsetStartIndex))
            : values.slice(startIndex, endIndex);

        // If we have an index but no key, return values object at index
        if (isNumber(index) && !key) {
            return index >= 0
                ? get(values, `[${index}]`, {})
                : get(values, `[${values.length - 1}]`, {});
        }

        // If we have a find object but no key, return found values object
        if (findObj && !key) {
            return find(values, findObj) || {};
        }

        // If we have a find object and key, return value of key in found object
        if (findObj && key) {
            const valuesObj = find(values, findObj);
            const value = get(valuesObj, key, noDataPlaceholder);
            return format
                ? formatter({
                      dataStore,
                      locale,
                      value,
                      type: format.type,
                      currency: fundCurrency,
                      options: format.options,
                  })
                : value;
        }

        // If we have an index and key, return value at values[index].key
        if (isNumber(index) && key) {
            const value =
                index >= 0
                    ? get(values, `[${index}].${key}`, noDataPlaceholder)
                    : get(
                          values,
                          `[${values.length + index}].${key}`,
                          noDataPlaceholder
                      );

            return format
                ? formatter({
                      dataStore,
                      locale,
                      value,
                      type: format.type,
                      currency: fundCurrency,
                      options: format.options,
                  })
                : value;
        }

        return values;
    }

    return noDataPlaceholder;
};

export default getDataPoint;



// WEBPACK FOOTER //
// ./src/utils/getDataPoint.ts